import type { AbilityService } from '@efdigital/ll-addon-auth';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import {
  buildCreateConsumedTool,
  buildUpdateConsumedTool,
} from '@efdigital/ll-admin-v2/data/builders/lti/lti-consumed-tools';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback';
import type { LtiConsumedTool } from '@efdigital/ll-admin-v2/data/types/lti';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

export class EditLtiConsumedTool extends Component<
  EditRecordSignature<
    LtiConsumedTool,
    {
      platformId: string;
      initialState: 'readonly' | 'edit';
    }
  > & {
    Blocks: {
      help: [];
    };
  }
> {
  @service declare store: Store;
  @service declare ability: AbilityService;

  get clientId(): string {
    return this.args.item.platformData.client_id;
  }

  get jwksUrl(): string {
    return this.args.item.platformData.jwks_url;
  }

  get authEndpoint(): string {
    return this.args.item.platformData.authorizationEndpoint;
  }

  setClientId = (event: Event) => {
    const client_id = (event.target as HTMLInputElement).value;
    this.args.item.platformData = {
      ...this.args.item.platformData,
      client_id,
    };
  };

  setJwksUrl = (event: Event) => {
    const jwks_url = (event.target as HTMLInputElement).value;
    this.args.item.platformData = {
      ...this.args.item.platformData,
      jwks_url,
    };
  };

  setAuthEndpoint = (event: Event) => {
    const authorizationEndpoint = (event.target as HTMLInputElement).value;
    this.args.item.platformData = {
      ...this.args.item.platformData,
      authorizationEndpoint,
    };
  };

  saveTool = () => {
    const tool = this.args.item;

    if (tool.isNew) {
      return this.store.request(buildCreateConsumedTool(tool));
    }

    return this.store.request(
      buildUpdateConsumedTool(tool, this.args.platformId)
    );
  };

  revertTool = () => {
    deepRollback(this.args.item, this.store);
  };

  <template>
    <EditRecord
      @editLabel="Edit tool settings"
      @item={{@item}}
      @context={{@context}}
      @canEdit={{true}}
      @maxDepth={{0}}
      @buttonSize="btn-md"
      @save={{this.saveTool}}
      @revert={{this.revertTool}}
      @initialState={{@initialState}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-0">
          <legend class="gud-eyebrow">Values from customer</legend>
          {{yield to="help"}}

          <div class="mb-3">
            <label class="form-label" for="{{uniqueId}}-client-id">Platform
              client ID</label>
            <input
              id="{{uniqueId}}-client-id"
              type="text"
              class="form-control font-monospace"
              required
              value={{this.clientId}}
              data-test-lti-tool-client-id
              {{on "input" this.setClientId}}
            />
          </div>
          <div class="mb-3">
            <label class="form-label" for="{{uniqueId}}-jwks-url">Platform JWKS
              URL</label>
            <input
              id="{{uniqueId}}-jwks-url"
              type="text"
              class="form-control font-monospace"
              required
              value={{this.jwksUrl}}
              data-test-lti-tool-jwks-url
              {{on "input" this.setJwksUrl}}
            />
          </div>
          <div class="mb-3">
            <label
              class="form-label"
              for="{{uniqueId}}-auth-endpoint"
            >Authorization endpoint</label>
            <input
              id="{{uniqueId}}-auth-endpoint"
              type="text"
              class="form-control font-monospace"
              required
              value={{this.authEndpoint}}
              data-test-lti-tool-auth-endpoint
              {{on "input" this.setAuthEndpoint}}
            />
          </div>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}

export default EditLtiConsumedTool;
